import { useEffect, useRef } from "react";

import { toggleHiddenSeries, withHiddenSeries } from "../../util";

// Set by ECharts on each legend item's group.
const isLegendItem = (el): boolean => {
  for (let node = el; node; node = node.parent) {
    if (node.__legendDataIndex != null) return true;
  }
  return false;
};

/**
 * A legend click hides that product's series, forecasts included, with the
 * axes left where they are: the item ECharts just unselected is selected
 * again and the series are drawn invisibly instead (see withHiddenSeries).
 *
 * ECharts renders that unselect, with animation, before `legendselectchanged`
 * fires, so the animation is already off while the pointer is over a legend
 * item.
 */
const useLegendHideSeries = (
  echarts,
  options,
  hiddenSeries: Maybe<string[]>,
  setOptionsToChart,
  onHiddenSeriesChange: (hiddenSeries: string[]) => void
) => {
  // Read at click time: a second click can land before the handler is bound again.
  const latest = useRef({
    options,
    hiddenSeries,
    setOptionsToChart,
    onHiddenSeriesChange
  });
  latest.current = { options, hiddenSeries, setOptionsToChart, onHiddenSeriesChange };

  useEffect(() => {
    if (!echarts) return;
    const zr = echarts.getZr();
    let overLegendItem = false;

    const setAnimation = (animation: boolean) => echarts.setOption({ animation });

    const onMouseOver = ({ target }) => {
      if (!isLegendItem(target)) return;
      overLegendItem = true;
      setAnimation(false);
    };
    const onMouseOut = ({ target }) => {
      if (!isLegendItem(target)) return;
      overLegendItem = false;
      setAnimation(true);
    };
    const onGlobalOut = () => {
      if (!overLegendItem) return;
      overLegendItem = false;
      setAnimation(true);
    };

    const onLegendToggle = ({ name }) => {
      const { options, hiddenSeries, setOptionsToChart, onHiddenSeriesChange } =
        latest.current;
      const next = toggleHiddenSeries(hiddenSeries, name);

      // A toggle the hover did not see still has its morph cut short here.
      setAnimation(false);
      echarts.dispatchAction({ type: "legendSelect", name });
      // Applied here, not from state, so the re-selected line never paints.
      setOptionsToChart(withHiddenSeries(options, next));
      if (!overLegendItem) setAnimation(true);
      onHiddenSeriesChange(next);
    };

    zr.on("mouseover", onMouseOver);
    zr.on("mouseout", onMouseOut);
    zr.on("globalout", onGlobalOut);
    echarts.on("legendselectchanged", onLegendToggle);

    return () => {
      zr.off("mouseover", onMouseOver);
      zr.off("mouseout", onMouseOut);
      zr.off("globalout", onGlobalOut);
      echarts.off("legendselectchanged", onLegendToggle);
    };
  }, [echarts]);
};

export default useLegendHideSeries;
